import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { useRespondToChallenge } from '../hooks/useChallenges'
import { ChallengeInviteForm } from './ChallengeInviteForm'
import type { Challenge } from '../types'

interface ChallengeCardProps {
  readonly challenge: Challenge
}

export function ChallengeCard({ challenge }: ChallengeCardProps) {
  const { t, i18n } = useTranslation()
  const respond = useRespondToChallenge()

  const deadline = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
    new Date(challenge.ends_at)
  )

  const answer = (value: 'accept' | 'decline' | 'leave') =>
    respond.mutate({ challengeId: challenge.id, answer: value })

  const ranking = [...challenge.participants].sort((a, b) => b.score - a.score)

  return (
    <li className="flex flex-col gap-3 rounded-2xl border border-outline-variant bg-surface px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-display font-semibold text-on-surface">
          {challenge.course_title ?? challenge.subject}
        </span>
        <span className="text-xs text-on-surface-variant">
          {challenge.status === 'resolved'
            ? t('challenges.ended')
            : t('challenges.ends_on', { date: deadline })}
        </span>
      </div>

      <p className="text-xs text-on-surface-variant">
        {t(`challenges.metrics.${challenge.metric}`)}
      </p>

      <ul className="flex flex-col gap-1">
        {ranking.map((participant) => (
          <li
            key={participant.username ?? participant.status}
            className="flex items-center justify-between gap-3 text-sm"
          >
            <span className="truncate text-on-surface">
              {participant.username}
              {participant.status !== 'accepted' && (
                <span className="ml-2 text-xs text-on-surface-variant">
                  {t(`challenges.participant_status.${participant.status}`)}
                </span>
              )}
            </span>
            <span className="tabular-nums font-semibold text-secondary">{participant.score}</span>
          </li>
        ))}
      </ul>

      {challenge.status === 'resolved' && (
        <p className="text-sm font-semibold text-secondary">
          {challenge.winner
            ? t('challenges.winner', { username: challenge.winner })
            : t('challenges.no_winner')}
        </p>
      )}

      {challenge.status === 'active' && challenge.my_status === 'invited' && (
        <span className="flex gap-2">
          <Button size="sm" onClick={() => answer('accept')} isLoading={respond.isPending}>
            {t('challenges.accept')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => answer('decline')}>
            {t('challenges.decline')}
          </Button>
        </span>
      )}

      {challenge.status === 'active' && challenge.my_status === 'accepted' && (
        <span className="flex flex-wrap items-center gap-2">
          {challenge.is_creator && <ChallengeInviteForm challengeId={challenge.id} />}
          <Button size="sm" variant="ghost" onClick={() => answer('leave')}>
            {t('challenges.leave')}
          </Button>
        </span>
      )}
    </li>
  )
}
